import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/apiService';
import { VisaChatSession } from './useVisaChat';

interface UseRecentChatsReturn {
    recentChats: VisaChatSession[];
    isLoading: boolean;
    error: string | null;
    refreshChats: () => Promise<void>;
}

export const useRecentChats = (limit: number = 5): UseRecentChatsReturn => {
    const { currentUser } = useAuth();
    const [recentChats, setRecentChats] = useState<VisaChatSession[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fetchRecentChats = useCallback(async () => {
        if (!currentUser) {
            setRecentChats([]);
            return;
        }

        try {
            setIsLoading(true);
            setError(null);

            // Fetch from Global Talent Visa only (Tourist Visa is coming soon)
            const gtvResponse = await apiService.get<{
                status: string;
                chat_sessions: VisaChatSession[];
            }>(`/api/domain/global_talent_visa/chat/sessions?limit=${limit}`);

            const allSessions: VisaChatSession[] = [];

            // Add Global Talent Visa sessions
            if (gtvResponse.status === 'success') {
                allSessions.push(...gtvResponse.chat_sessions);
            }

            // Sort by updatedAt and take the most recent ones
            const sortedSessions = allSessions
                .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
                .slice(0, limit);

            setRecentChats(sortedSessions);

        } catch (err) {
            console.error('Error fetching recent chats:', err);
            setError('Failed to load recent chats');
            setRecentChats([]);
        } finally {
            setIsLoading(false);
        }
    }, [currentUser, limit]);

    const refreshChats = useCallback(async () => {
        await fetchRecentChats();
    }, [fetchRecentChats]);

    // Load recent chats when user changes
    useEffect(() => {
        fetchRecentChats();
    }, [fetchRecentChats]);

    return {
        recentChats,
        isLoading,
        error,
        refreshChats
    };
}; 